import { ReactElement, useMemo, useState, useCallback, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  Typography,
  Chip,
  Alert,
  Skeleton,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
} from '@mui/material'
import {
  DataGridPremium,
  GridRowSelectionModel,
} from '@mui/x-data-grid-premium'
import DataGridToolbar from 'src/v3/components/data-grid-toolbar'
import { NavigateNext, ArrowBack, Cancel } from '@mui/icons-material'
import { useScrollToTop } from 'src/v3/utilities/hooks/use-scroll-to-top'
import UserFilters from '../../components/user-filters'
import { dataGridSx, paperInputSx } from '../../styles'
import {
  userColumns,
  DATAGRID_DEFAULT_PAGE_SIZE,
  DATAGRID_PAGE_SIZE_OPTIONS,
} from '../../constants'

type User = {
  id: string
  firstName: string
  lastName: string
  primaryEmail: string
}

type Group = {
  id: string
  name: string
  users: string[]
}

type UserRow = {
  id: string
  name: string
  email: string
  group: string
}

type FormValues = {
  userIDs: string[]
}

type FormErrors = {
  userIDs?: string
}

type UserSelectionStepProps = {
  values: FormValues
  errors: FormErrors
  users: User[]
  groups: Group[]
  onValueChange: (field: keyof FormValues, value: any) => void
  onCancel: () => void
  onBack: () => void
  onContinue: () => void
  canContinue: boolean
  isLoadingUsers?: boolean
}

export default function AssignmentUserSelectionStep({
  values,
  errors,
  users,
  groups,
  onValueChange,
  onCancel,
  onBack,
  onContinue,
  canContinue,
  isLoadingUsers = false,
}: UserSelectionStepProps): ReactElement {
  let UserSelectionToolbar = useCallback(
    () => (
      <DataGridToolbar
        fileName="assignment-user-selection"
        showQuickFilter={false}
      />
    ),
    [],
  )

  let [searchQuery, setSearchQuery] = useState('')
  let [groupFilter, setGroupFilter] = useState('')
  let [initiallySortedRows, setInitiallySortedRows] = useState<UserRow[]>([])

  let userGroupMap = useMemo(() => {
    let map = new Map<string, string[]>()
    groups.forEach((group) => {
      group.users.forEach((userId) => {
        let existingGroups = map.get(userId) || []
        map.set(userId, [...existingGroups, group.name])
      })
    })
    return map
  }, [groups])

  let userRows = useMemo<UserRow[]>(() => {
    let rows = users.map((user) => {
      let userGroups = userGroupMap.get(user.id) || []
      return {
        id: user.id,
        name: `${user.firstName} ${user.lastName}`,
        email: user.primaryEmail,
        group: userGroups.length > 0 ? userGroups.join(', ') : '-',
      }
    })
    return rows
  }, [users, userGroupMap])

  useEffect(() => {
    if (userRows.length > 0 && initiallySortedRows.length === 0) {
      let selectedSet = new Set(values.userIDs)

      let sorted = userRows.toSorted((a, b) => {
        let aSelected = selectedSet.has(a.id)
        let bSelected = selectedSet.has(b.id)

        if (aSelected && !bSelected) return -1
        if (!aSelected && bSelected) return 1
        return a.name.localeCompare(b.name)
      })

      setInitiallySortedRows(sorted)
    }
  }, [userRows, initiallySortedRows.length, values.userIDs])

  let filteredUsers = useMemo(() => {
    let query = searchQuery.toLowerCase()

    return initiallySortedRows.filter((user) => {
      if (groupFilter) {
        let group = groups.find((g) => g.id === groupFilter)
        if (group && !group.users.includes(user.id)) return false
      }

      if (
        searchQuery &&
        !user.name.toLowerCase().includes(query) &&
        !user.email.toLowerCase().includes(query)
      )
        return false

      return true
    })
  }, [initiallySortedRows, groupFilter, searchQuery, groups])

  let stats = useMemo(() => {
    let selectedUserRows = userRows.filter((u) => values.userIDs.includes(u.id))

    return {
      userCount: selectedUserRows.length,
    }
  }, [userRows, values.userIDs])

  let selectedGroupIDs = useMemo(
    () =>
      groups
        .filter((group) => {
          let validUserIDs = group.users.filter((userId) =>
            users.some((user) => user.id === userId),
          )
          return (
            validUserIDs.length > 0 &&
            validUserIDs.every((id) => values.userIDs.includes(id))
          )
        })
        .map((group) => group.id),
    [groups, users, values.userIDs],
  )

  let handleSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      let filteredIDs = new Set(filteredUsers.map((row) => row.id))
      let hiddenSelectedIDs = values.userIDs.filter(
        (id) => !filteredIDs.has(id),
      )

      let visibleSelectedIDs =
        newSelection?.type === 'exclude'
          ? (() => {
              let excludedIDs = new Set(
                Array.from(newSelection.ids || []) as string[],
              )
              return filteredUsers
                .map((row) => row.id)
                .filter((id) => !excludedIDs.has(id))
            })()
          : (Array.from(newSelection.ids) as string[])

      onValueChange('userIDs', [
        ...new Set([...hiddenSelectedIDs, ...visibleSelectedIDs]),
      ])
    },
    [onValueChange, filteredUsers, values.userIDs],
  )

  let handleResetFilters = useCallback(() => {
    setSearchQuery('')
    setGroupFilter('')
  }, [])

  let handleGroupChange = useCallback(
    (event: any) => {
      let newValue = event.target.value as string[]
      let added = newValue.filter((id) => !selectedGroupIDs.includes(id))
      let removed = selectedGroupIDs.filter((id) => !newValue.includes(id))

      let newUserSelection = new Set(values.userIDs)

      added.forEach((groupId) => {
        let group = groups.find((g) => g.id === groupId)
        if (!group) return

        let validUserIDs = group.users.filter((userId) =>
          users.some((user) => user.id === userId),
        )
        validUserIDs.forEach((id) => newUserSelection.add(id))
      })

      removed.forEach((groupId) => {
        let group = groups.find((g) => g.id === groupId)
        if (!group) return

        let validUserIDs = group.users.filter((userId) =>
          users.some((user) => user.id === userId),
        )
        validUserIDs.forEach((id) => newUserSelection.delete(id))
      })

      onValueChange('userIDs', Array.from(newUserSelection))
    },
    [selectedGroupIDs, values.userIDs, groups, users, onValueChange],
  )

  let groupsWithUsers = useMemo(
    () => groups.filter((group) => group.users && group.users.length > 0),
    [groups],
  )

  let hasGroups = groupsWithUsers && groupsWithUsers.length > 0

  useScrollToTop()

  return (
    <>
      <Card className="mb-6">
        <Box className="p-6">
          <Box className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <Typography variant="h6" component="h2">
              Select Users
            </Typography>
            {(searchQuery || groupFilter) && (
              <Button
                className="whitespace-nowrap"
                variant="outlined"
                color="secondary"
                size="medium"
                onClick={handleResetFilters}
                aria-label="Reset filters"
                sx={{ height: 32 }}
              >
                Reset Filters
              </Button>
            )}
          </Box>

          <UserFilters
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
            groupFilter={groupFilter}
            onGroupChange={setGroupFilter}
            groups={groupsWithUsers}
          />

          {hasGroups && (
            <Box className="mb-4">
              <FormControl fullWidth sx={paperInputSx}>
                <InputLabel id="group-label">Select Users by Group</InputLabel>
                <Select
                  labelId="group-label"
                  multiple
                  value={selectedGroupIDs}
                  onChange={handleGroupChange}
                  renderValue={(selected) =>
                    `${selected.length} group${selected.length !== 1 ? 's' : ''} selected`
                  }
                  label="Select Users by Group"
                  MenuProps={{
                    autoFocus: false,
                    disableAutoFocusItem: true,
                    variant: 'menu',
                  }}
                >
                  {groupsWithUsers.map((group) => {
                    let validUserIDs = group.users.filter((userId) =>
                      users.some((user) => user.id === userId),
                    )

                    if (validUserIDs.length === 0) return null

                    return (
                      <MenuItem key={group.id} value={group.id}>
                        <Checkbox
                          checked={selectedGroupIDs.includes(group.id)}
                        />
                        <ListItemText
                          primary={`${group.name} (${validUserIDs.length} ${validUserIDs.length === 1 ? 'user' : 'users'})`}
                        />
                      </MenuItem>
                    )
                  })}
                </Select>
              </FormControl>
            </Box>
          )}

          {errors.userIDs && (
            <Alert severity="error" className="mb-4" role="alert">
              {errors.userIDs}
            </Alert>
          )}

          <Box
            className="flex flex-wrap items-center justify-end gap-2 mb-3"
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <Chip
              label={`${stats.userCount} user${stats.userCount !== 1 ? 's' : ''} selected`}
              size="medium"
              color="primary"
              aria-label={`${stats.userCount} ${stats.userCount !== 1 ? 'users' : 'user'} selected`}
            />
          </Box>
        </Box>

        {isLoadingUsers ? (
          <Skeleton
            variant="rectangular"
            height={400}
            sx={{ borderRadius: 1 }}
            aria-label="Loading users"
          />
        ) : (
          <DataGridPremium
            rows={filteredUsers}
            columns={userColumns}
            slots={{ toolbar: UserSelectionToolbar }}
            showToolbar
            checkboxSelection
            disableRowSelectionOnClick
            rowSelectionModel={{
              type: 'include',
              ids: new Set(values.userIDs),
            }}
            onRowSelectionModelChange={handleSelectionChange}
            pagination
            initialState={{
              pagination: {
                paginationModel: { pageSize: DATAGRID_DEFAULT_PAGE_SIZE },
              },
            }}
            pageSizeOptions={DATAGRID_PAGE_SIZE_OPTIONS}
            sx={dataGridSx}
            aria-label="Select users for assignment"
          />
        )}
      </Card>

      <Box className="flex justify-end gap-3">
        <Button
          variant="outlined"
          onClick={onCancel}
          size="large"
          startIcon={<Cancel />}
        >
          Cancel
        </Button>
        <Button
          variant="outlined"
          onClick={onBack}
          size="large"
          startIcon={<ArrowBack />}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={onContinue}
          disabled={!canContinue}
          size="large"
          endIcon={<NavigateNext />}
        >
          Continue to Review
        </Button>
      </Box>
    </>
  )
}
